import React, { useState, useEffect } from 'react';
import {
  X,
  Dumbbell,
  Heart,
  CheckCircle2,
  Trash2,
  Clock,
  Sparkles,
  ArrowRight,
  Footprints,
  Activity,
  Bike,
  Zap,
  Waves,
  Trophy,
  HeartPulse,
  MinusCircle,
  AlertTriangle,
} from 'lucide-react';
import { Exercise, ExerciseIntensity, DigestiveEffect, ExerciseTiming } from '../lib/types';
import { formatIsoDate } from '../lib/dateUtils';
import { triggerHaptic } from '../lib/haptics';
import { Sheet } from './Sheet';

interface ExerciseLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingExercise?: Exercise | null;
  onDeleteExercise?: (id: string) => Promise<void> | void;
  onSaveExercise: (
    exercise: Omit<Exercise, 'id'>,
    id?: string
  ) => Promise<void> | void;
}

const EXERCISE_TYPES: { id: string; label: string; icon: React.FC<{ className?: string }> }[] = [
  { id: 'Walking', label: 'Walking', icon: Footprints },
  { id: 'Running', label: 'Running', icon: Activity },
  { id: 'Cycling', label: 'Cycling', icon: Bike },
  { id: 'Gym / Weights', label: 'Gym / Weights', icon: Dumbbell },
  { id: 'Yoga / Stretch', label: 'Yoga / Stretch', icon: Sparkles },
  { id: 'HIIT / Cardio', label: 'HIIT / Cardio', icon: Zap },
  { id: 'Swimming', label: 'Swimming', icon: Waves },
  { id: 'Sports', label: 'Sports / Game', icon: Trophy },
];

const TIMING_OPTIONS: { id: ExerciseTiming; label: string; desc: string }[] = [
  { id: 'Post-Meal Walk', label: 'Post-Meal Walk', desc: 'Accelerates gastric emptying & lowers bloat' },
  { id: 'Before Eating', label: 'Before Eating', desc: 'Pre-meal physical activity' },
  { id: 'Fasted', label: 'Fasted Morning', desc: 'Before breakfast' },
  { id: 'General Activity', label: 'General Activity', desc: 'Independent of meal timing' },
];

const DIGESTIVE_EFFECTS: { id: DigestiveEffect; label: string; icon: React.FC<{ className?: string }>; color: string }[] = [
  { id: 'Relieved Bloating', label: 'Relieved Bloating', icon: CheckCircle2, color: 'emerald' },
  { id: 'Improved Digestion', label: 'Improved Digestion', icon: HeartPulse, color: 'teal' },
  { id: 'Neutral', label: 'No Noticeable Change', icon: MinusCircle, color: 'slate' },
  { id: 'Triggered Cramps / Discomfort', label: 'Triggered Cramps', icon: AlertTriangle, color: 'amber' },
];

export const ExerciseLogModal: React.FC<ExerciseLogModalProps> = ({
  isOpen,
  onClose,
  editingExercise,
  onDeleteExercise,
  onSaveExercise,
}) => {
  const [selectedType, setSelectedType] = useState('Walking');
  const [customType, setCustomType] = useState('');
  const [duration, setDuration] = useState<number>(30);
  const [intensity, setIntensity] = useState<ExerciseIntensity>('Moderate');
  const [timing, setTiming] = useState<ExerciseTiming | undefined>('Post-Meal Walk');
  const [digestiveEffect, setDigestiveEffect] = useState<DigestiveEffect | undefined>('Relieved Bloating');
  const [notes, setNotes] = useState('');
  const [dateStr, setDateStr] = useState('');
  const [timeStr, setTimeStr] = useState('');
  const [showAppleHealthInfo, setShowAppleHealthInfo] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Sync state when modal opens or editingExercise changes
  useEffect(() => {
    if (!isOpen) {
      setShowDeleteConfirm(false);
      return;
    }

    if (editingExercise) {
      const isKnown = EXERCISE_TYPES.some((t) => t.id === editingExercise.activity_type);
      if (isKnown) {
        setSelectedType(editingExercise.activity_type);
        setCustomType('');
      } else {
        setSelectedType('Custom');
        setCustomType(editingExercise.activity_type);
      }
      setDuration(editingExercise.duration_minutes || 30);
      setIntensity(editingExercise.intensity || 'Moderate');
      setTiming(editingExercise.timing_relative_to_meal || 'General Activity');
      setDigestiveEffect(editingExercise.digestive_effect);
      setNotes(editingExercise.notes || '');

      const d = new Date(editingExercise.timestamp_iso || Date.now());
      setDateStr(editingExercise.date || formatIsoDate(d));
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      setTimeStr(`${hh}:${mm}`);
    } else {
      setSelectedType('Walking');
      setCustomType('');
      setDuration(30);
      setIntensity('Moderate');
      setTiming('Post-Meal Walk');
      setDigestiveEffect('Relieved Bloating');
      setNotes('');

      const now = new Date();
      setDateStr(formatIsoDate(now));
      const hh = String(now.getHours()).padStart(2, '0');
      const mm = String(now.getMinutes()).padStart(2, '0');
      setTimeStr(`${hh}:${mm}`);
    }
  }, [isOpen, editingExercise]);

  const handleSave = async () => {
    setIsSaving(true);
    triggerHaptic('medium');

    const activity = selectedType === 'Custom' ? customType.trim() || 'Exercise' : selectedType;

    // Reconstruct ISO timestamp from dateStr and timeStr
    let timestampIso = new Date().toISOString();
    try {
      if (dateStr && timeStr) {
        const [hours, minutes] = timeStr.split(':').map(Number);
        const [y, m, d] = dateStr.split('-').map(Number);
        const dt = new Date(y, m - 1, d, hours || 0, minutes || 0);
        timestampIso = dt.toISOString();
      }
    } catch {
      timestampIso = new Date().toISOString();
    }

    const payload: Omit<Exercise, 'id'> = {
      activity_type: activity,
      duration_minutes: Number(duration) || 15,
      intensity,
      timing_relative_to_meal: timing,
      digestive_effect: digestiveEffect,
      notes: notes.trim() || undefined,
      date: dateStr || formatIsoDate(new Date()),
      timestamp_iso: timestampIso,
    };

    await onSaveExercise(payload, editingExercise?.id);
    setIsSaving(false);
    onClose();
  };

  const handleDelete = async () => {
    if (!editingExercise?.id || !onDeleteExercise) return;
    setIsDeleting(true);
    triggerHaptic('heavy');
    await onDeleteExercise(editingExercise.id);
    setIsDeleting(false);
    onClose();
  };

  return (
    <Sheet isOpen={isOpen} onClose={onClose} ariaLabel="Log exercise">

        {/* Header */}
        <div className="px-6 py-3.5 flex items-center justify-between border-b border-slate-200/60 dark:border-zinc-800">
          <div className="flex items-center space-x-2.5">
            <div className="w-8 h-8 rounded-xl bg-teal-500/10 text-teal-600 dark:text-teal-400 flex items-center justify-center">
              <Dumbbell className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-base font-black uppercase tracking-tight">
                {editingExercise ? 'Edit Exercise' : 'Log Exercise'}
              </h2>
              <p className="text-[10px] text-slate-400 dark:text-zinc-500 font-medium">
                Track digestive movement & symptom relief
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-200/70 dark:bg-zinc-800 flex items-center justify-center text-slate-700 dark:text-zinc-300 hover:text-rose-500 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="p-6 space-y-5 overflow-y-auto overscroll-contain min-h-0">
          {/* Clinical Motility Banner */}
          <div className="p-3.5 rounded-2xl bg-gradient-to-r from-teal-500/10 via-emerald-500/10 to-transparent border border-teal-500/20 text-xs space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 font-bold text-teal-700 dark:text-teal-300">
                <Sparkles className="w-4 h-4 text-emerald-500" />
                <span>Digestive Transit & Exercise</span>
              </div>
              <button
                type="button"
                onClick={() => setShowAppleHealthInfo((prev) => !prev)}
                className="text-[10px] font-bold text-teal-600 dark:text-teal-400 hover:underline"
              >
                {showAppleHealthInfo ? 'Hide details' : 'Apple Fitness & Health'}
              </button>
            </div>
            <p className="text-[11px] text-slate-600 dark:text-zinc-300 leading-relaxed font-medium">
              A 15–30 minute walk after eating accelerates gastric emptying and stimulates colonic peristalsis, helping clear trapped gas and reduce bloating.
            </p>
            {showAppleHealthInfo && (
              <div className="pt-2 border-t border-teal-500/20 text-[11px] text-slate-600 dark:text-zinc-300 space-y-1.5 animate-fade-in">
                <div className="flex items-center space-x-1.5 text-rose-500 font-bold">
                  <Heart className="w-3.5 h-3.5 fill-rose-500" />
                  <span>Apple Fitness & Health Sync</span>
                </div>
                <p>
                  Apple restricts web apps in browsers from directly reading HealthKit credentials. When packaged as a native iOS app, workouts sync automatically in the background. Here in web mode, logging your activity factors directly into the digestive analysis engine!
                </p>
              </div>
            )}
          </div>

          {/* Activity Type Selection */}
          <div className="space-y-2">
            <span className="text-[11px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500 block">
              Activity Type
            </span>
            <div className="grid grid-cols-4 gap-2">
              {EXERCISE_TYPES.map((t) => {
                const isSelected = selectedType === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      triggerHaptic('light');
                      setSelectedType(t.id);
                    }}
                    className={`p-2.5 rounded-2xl border flex flex-col items-center justify-center space-y-1 transition-all active:scale-95 ${
                      isSelected
                        ? 'bg-slate-950 dark:bg-white text-white dark:text-black border-slate-950 dark:border-white shadow-sm font-bold'
                        : 'bg-white dark:bg-[#1A1D24] text-slate-700 dark:text-zinc-300 border-slate-200 dark:border-zinc-800 hover:border-slate-300'
                    }`}
                  >
                    <t.icon className={`w-5 h-5 ${isSelected ? 'text-inherit' : 'text-slate-600 dark:text-zinc-400'}`} />
                    <span className="text-[10px] font-bold truncate w-full text-center">{t.label}</span>
                  </button>
                );
              })}
            </div>
            {selectedType === 'Custom' && (
              <input
                type="text"
                value={customType}
                onChange={(e) => setCustomType(e.target.value)}
                placeholder="Enter workout name (e.g. Pilates, Rowing, Hike)"
                className="w-full mt-2 px-3.5 py-2.5 bg-white dark:bg-[#1A1D24] border border-slate-200 dark:border-zinc-800 rounded-xl text-xs text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            )}
          </div>

          {/* Duration & Intensity in Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Duration Selector */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                  Duration
                </span>
                <span className="text-xs font-black text-slate-900 dark:text-white">
                  {duration} mins
                </span>
              </div>
              <div className="grid grid-cols-4 gap-1.5">
                {[15, 30, 45, 60].map((mins) => (
                  <button
                    key={mins}
                    type="button"
                    onClick={() => {
                      triggerHaptic('light');
                      setDuration(mins);
                    }}
                    className={`py-2 rounded-xl text-xs font-bold transition-all ${
                      duration === mins
                        ? 'bg-slate-950 dark:bg-white text-white dark:text-black shadow-xs font-black'
                        : 'bg-white dark:bg-[#1A1D24] border border-slate-200 dark:border-zinc-800 text-slate-700 dark:text-zinc-300'
                    }`}
                  >
                    {mins}m
                  </button>
                ))}
              </div>
            </div>

            {/* Intensity Selector */}
            <div className="space-y-2">
              <span className="text-[11px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500 block">
                Intensity
              </span>
              <div className="grid grid-cols-3 gap-1.5">
                {(['Light', 'Moderate', 'High'] as const).map((lvl) => (
                  <button
                    key={lvl}
                    type="button"
                    onClick={() => {
                      triggerHaptic('light');
                      setIntensity(lvl);
                    }}
                    className={`py-2 px-1 rounded-xl text-xs font-bold transition-all text-center ${
                      intensity === lvl
                        ? 'bg-teal-600 text-white font-black shadow-xs'
                        : 'bg-white dark:bg-[#1A1D24] border border-slate-200 dark:border-zinc-800 text-slate-700 dark:text-zinc-300'
                    }`}
                  >
                    {lvl}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Timing Relative to Food */}
          <div className="space-y-2">
            <span className="text-[11px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500 block">
              When was this? (Meal Timing Context)
            </span>
            <div className="grid grid-cols-2 gap-2">
              {TIMING_OPTIONS.map((opt) => {
                const isSelected = timing === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      triggerHaptic('light');
                      setTiming(opt.id);
                    }}
                    className={`p-2.5 rounded-xl border text-left transition-all active:scale-[0.98] ${
                      isSelected
                        ? 'bg-teal-50 dark:bg-teal-950/40 border-teal-500 text-teal-900 dark:text-teal-200 shadow-xs'
                        : 'bg-white dark:bg-[#1A1D24] border-slate-200 dark:border-zinc-800 text-slate-700 dark:text-zinc-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs font-bold">{opt.label}</span>
                      {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" />}
                    </div>
                    <p className="text-[10px] text-slate-400 dark:text-zinc-500 line-clamp-1">
                      {opt.desc}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Perceived Digestive Effect */}
          <div className="space-y-2">
            <span className="text-[11px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500 block">
              Stomach / Digestion Effect
            </span>
            <div className="grid grid-cols-2 gap-2">
              {DIGESTIVE_EFFECTS.map((eff) => {
                const isSelected = digestiveEffect === eff.id;
                return (
                  <button
                    key={eff.id}
                    type="button"
                    onClick={() => {
                      triggerHaptic('light');
                      setDigestiveEffect(isSelected ? undefined : eff.id);
                    }}
                    className={`p-2.5 rounded-xl border text-left transition-all flex items-center space-x-2 ${
                      isSelected
                        ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-500 text-emerald-900 dark:text-emerald-200 shadow-xs font-bold'
                        : 'bg-white dark:bg-[#1A1D24] border-slate-200 dark:border-zinc-800 text-slate-700 dark:text-zinc-300'
                    }`}
                  >
                    <eff.icon className={`w-4 h-4 shrink-0 ${isSelected ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500'}`} />
                    <span className="text-[11px] leading-tight truncate font-bold">{eff.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time & Date adjustments */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500 block">
                Date
              </label>
              <input
                type="date"
                value={dateStr}
                onChange={(e) => setDateStr(e.target.value)}
                className="w-full px-3 py-2 bg-white dark:bg-[#1A1D24] border border-slate-200 dark:border-zinc-800 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500 block">
                Time
              </label>
              <input
                type="time"
                value={timeStr}
                onChange={(e) => setTimeStr(e.target.value)}
                className="w-full px-3 py-2 bg-white dark:bg-[#1A1D24] border border-slate-200 dark:border-zinc-800 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <span className="text-[11px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500 block">
              Notes (Optional)
            </span>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. 20-min brisk walk after pasta dinner, helped gas move"
              className="w-full px-3.5 py-2.5 bg-white dark:bg-[#1A1D24] border border-slate-200 dark:border-zinc-800 rounded-xl text-xs text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          {/* Delete Confirm if Editing */}
          {editingExercise && (
            <div className="pt-2 border-t border-slate-200 dark:border-zinc-800">
              {showDeleteConfirm ? (
                <div className="p-3 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 space-y-2">
                  <p className="text-xs font-bold text-rose-700 dark:text-rose-300">
                    Delete this exercise record?
                  </p>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="flex-1 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold transition-colors"
                    >
                      {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(false)}
                      className="flex-1 py-2 rounded-lg bg-slate-200 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 text-xs font-bold"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full py-2.5 rounded-xl border border-rose-200 dark:border-rose-900/50 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/20 text-xs font-bold flex items-center justify-center space-x-1.5 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Delete Exercise Log</span>
                </button>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="pt-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="w-full py-3.5 rounded-2xl bg-teal-600 hover:bg-teal-700 text-white font-black text-xs uppercase tracking-wider shadow-lg active:scale-95 transition-all flex items-center justify-center space-x-2"
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>
                {isSaving
                  ? 'Saving...'
                  : editingExercise
                  ? 'Update Exercise Log'
                  : 'Record Exercise Log'}
              </span>
            </button>
          </div>
        </div>
    </Sheet>
  );
};
